
import Layout from "@/components/layout/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, Star, Award, Shield } from "lucide-react";

const instructors = [
  {
    id: 1,
    name: "David Thompson",
    role: "Lead Instructor",
    experience: "15+ years",
    specialties: ["Defensive Driving", "Pass Plus", "Nervous Drivers"],
    rating: 4.9,
    reviews: 328,
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=300&h=300&q=80",
    bio: "David has been teaching driving for over 15 years and is known for his patience and calm approach. He specializes in helping nervous drivers gain confidence on the road."
  },
  {
    id: 2,
    name: "Sarah Williams",
    role: "Senior Instructor",
    experience: "12 years",
    specialties: ["Intensive Courses", "Theory Test Prep", "Advanced Driving"],
    rating: 4.8,
    reviews: 273,
    image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=300&h=300&q=80",
    bio: "Sarah is an expert in intensive courses and has helped hundreds of students pass their test quickly. Her teaching style is dynamic and engaging."
  },
  {
    id: 3,
    name: "Michael Roberts",
    role: "Instructor",
    experience: "8 years",
    specialties: ["New Drivers", "Refresher Courses", "Motorway Driving"],
    rating: 4.7,
    reviews: 189,
    image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=300&h=300&q=80",
    bio: "Michael's friendly approach makes him popular with new drivers. He has a talent for explaining complex concepts in simple terms."
  },
  {
    id: 4,
    name: "Emma Johnson",
    role: "Senior Instructor",
    experience: "10 years",
    specialties: ["Test Routes", "Maneuvers", "City Driving"],
    rating: 4.9,
    reviews: 215,
    image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=300&h=300&q=80",
    bio: "Emma is known for her detailed knowledge of test routes and common test scenarios. Her students consistently pass with high marks."
  },
  {
    id: 5,
    name: "James Wilson",
    role: "Instructor",
    experience: "7 years",
    specialties: ["Automatic Vehicles", "Eco-Driving", "Elderly Drivers"],
    rating: 4.8,
    reviews: 176,
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=300&h=300&q=80",
    bio: "James specializes in teaching in automatic vehicles and is particularly good with older learners returning to driving."
  },
  {
    id: 6,
    name: "Olivia Chen",
    role: "Instructor",
    experience: "6 years",
    specialties: ["Young Drivers", "Pass Plus", "Rural Driving"],
    rating: 4.7,
    reviews: 163,
    image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=300&h=300&q=80",
    bio: "Olivia has a great rapport with younger drivers and specializes in rural and country road driving techniques."
  },
  {
    id: 7,
    name: "Robert Martinez",
    role: "Senior Instructor",
    experience: "14 years",
    specialties: ["Advanced Techniques", "Defensive Driving", "Performance Driving"],
    rating: 4.9,
    reviews: 289,
    image: "https://images.unsplash.com/photo-1552058544-f2b08422138a?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=300&h=300&q=80",
    bio: "With a background in advanced and performance driving, Robert brings unique expertise to his teaching while maintaining a focus on safety."
  },
  {
    id: 8,
    name: "Sophia Taylor",
    role: "Instructor",
    experience: "5 years",
    specialties: ["Anxious Learners", "Theory Test", "City Navigation"],
    rating: 4.8,
    reviews: 142,
    image: "https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=300&h=300&q=80",
    bio: "Sophia is particularly skilled at helping anxious learners overcome their fears. Her patient approach puts even the most nervous students at ease."
  }
];

const Instructors = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-drivewise-900 to-drivewise-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Meet Our Instructors</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Our team of highly qualified and experienced driving instructors are dedicated to helping you become a confident, safe driver.
          </p>
        </div>
      </section>

      {/* Instructor Features */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-road-900 mb-4">Why Choose Our Instructors?</h2>
            <p className="text-xl text-road-600 max-w-3xl mx-auto">
              All our instructors are fully qualified, regularly assessed, and committed to the highest standards of driving instruction.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <Award className="h-12 w-12 text-drivewise-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-road-900 mb-2">Fully Certified</h3>
              <p className="text-road-700">
                All instructors are ADI registered with regular quality assessments
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <Calendar className="h-12 w-12 text-drivewise-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-road-900 mb-2">Experienced</h3>
              <p className="text-road-700">
                Our team has a combined 150+ years of teaching experience
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <Shield className="h-12 w-12 text-drivewise-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-road-900 mb-2">Background Checked</h3>
              <p className="text-road-700">
                All instructors undergo thorough background and reference checks
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <Star className="h-12 w-12 text-drivewise-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-road-900 mb-2">Highly Rated</h3>
              <p className="text-road-700">
                Our instructors maintain an average 4.8/5 star rating from students
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Instructor Profiles */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-road-900 mb-4">Our Instructor Team</h2>
            <p className="text-xl text-road-600 max-w-3xl mx-auto">
              Get to know the professionals who will guide you on your driving journey.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {instructors.map((instructor) => (
              <Card key={instructor.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-0">
                  <div className="relative">
                    <img 
                      src={instructor.image} 
                      alt={instructor.name} 
                      className="w-full h-64 object-cover object-center rounded-t-lg"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                      <h3 className="text-xl font-bold text-white">{instructor.name}</h3>
                      <p className="text-white/90">{instructor.role}</p>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="flex items-center text-yellow-500">
                        <Star className="h-5 w-5 fill-current" />
                        <span className="ml-1 font-semibold">{instructor.rating}</span>
                      </div>
                      <span className="mx-2 text-gray-400">•</span>
                      <span className="text-road-600">{instructor.reviews} reviews</span>
                      <span className="mx-2 text-gray-400">•</span>
                      <span className="text-road-600">{instructor.experience}</span>
                    </div>
                    
                    <p className="text-road-700 mb-4">
                      {instructor.bio}
                    </p>
                    
                    <div>
                      <h4 className="font-semibold text-road-900 mb-2">Specialties:</h4>
                      <div className="flex flex-wrap gap-2">
                        {instructor.specialties.map((specialty, index) => (
                          <span 
                            key={index} 
                            className="px-3 py-1 bg-drivewise-100 text-drivewise-800 rounded-full text-sm"
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Instructor Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-road-900 mb-4">What Students Say About Our Instructors</h2>
            <p className="text-xl text-road-600 max-w-3xl mx-auto">
              Our commitment to quality instruction is reflected in the feedback from our students.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg relative">
              <div className="text-drivewise-500 text-5xl absolute -top-4 -left-2">"</div>
              <p className="text-road-700 mb-6 z-10 relative">
                David's patience and clear instruction helped me overcome my driving anxiety. He knew exactly how to build my confidence gradually. I passed my test on the first attempt!
              </p>
              <div className="flex items-center">
                <img 
                  src="https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=100&h=100&q=80" 
                  alt="Student" 
                  className="h-10 w-10 rounded-full object-cover"
                />
                <div className="ml-3">
                  <h4 className="font-semibold text-road-900">Jessica P.</h4>
                  <p className="text-road-500 text-sm">Student of David Thompson</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg relative">
              <div className="text-drivewise-500 text-5xl absolute -top-4 -left-2">"</div>
              <p className="text-road-700 mb-6 z-10 relative">
                Sarah's intensive course was perfect for me. She managed to cover everything I needed to know in a short time while ensuring I was truly prepared. Her teaching style made complex maneuvers easy to understand.
              </p>
              <div className="flex items-center">
                <img 
                  src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=100&h=100&q=80" 
                  alt="Student" 
                  className="h-10 w-10 rounded-full object-cover"
                />
                <div className="ml-3">
                  <h4 className="font-semibold text-road-900">Alex T.</h4>
                  <p className="text-road-500 text-sm">Student of Sarah Williams</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg relative">
              <div className="text-drivewise-500 text-5xl absolute -top-4 -left-2">"</div>
              <p className="text-road-700 mb-6 z-10 relative">
                Michael is an exceptional instructor. His calm demeanor and positive reinforcement made each lesson enjoyable. He tailored his teaching to my learning style and always went the extra mile.
              </p>
              <div className="flex items-center">
                <img 
                  src="https://images.unsplash.com/photo-1541101767792-f9b2b1c4f127?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=100&h=100&q=80" 
                  alt="Student" 
                  className="h-10 w-10 rounded-full object-cover"
                />
                <div className="ml-3">
                  <h4 className="font-semibold text-road-900">Priya M.</h4>
                  <p className="text-road-500 text-sm">Student of Michael Roberts</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Instructors;
